import {FeatureCollection, Geometry} from "geojson";
import React from "react";
import USMapState from "../../model/geographic/USMapState";
import Dashboard from "../../model/Dashboard";
import {circlePath, getPath, normalizeStateName} from "./USMapUtils";
import {StateElectionDataEntry} from "../../model/data/StateElectionDataEntry";
import * as d3 from "d3";
import {interpolate} from "flubber";
import {USMapType} from "../../model/geographic/USMapType";
import {USMapInteractionData} from "./USMapToolTip";
import USElectionData from "../../model/data/USElectionData";

function createTooltipData(
    d: any,
    dashboard: Dashboard
): USMapInteractionData['data'] {
    const normalizedStateName = normalizeStateName(d.properties.name) as keyof StateElectionDataEntry;
    const data = dashboard.selectedElectionData.getStateElectionData(normalizedStateName);
    const winner = data.winner === 'dem' ? 'Democrats' : 'Republicans';
    const pointLead = data.point_lead;

    return [
        `${d.properties.name}: ${winner} won by ${pointLead}%`,
        `Total votes: ${Math.round(data.total_votes/1000)}k`,
        `Dem percentage: ${data.dem_percentage}%`,
        `Rep percentage: ${data.rep_percentage}%`,
        `Dem electoral votes: ${data.dem_electoral_votes}`,
        `Rep electoral votes: ${data.rep_electoral_votes}`,
        `Number of voters per electoral vote: ${Math.round(data.num_voters_per_electoral_vote/1000)}k`,
        `Year: ${dashboard.year}`
    ];
}

function getColor(d: any, dashboard: Dashboard) {
    const normalizedStateName = normalizeStateName(d.properties.name) as keyof StateElectionDataEntry;
    const data = dashboard.selectedElectionData.getStateElectionData(normalizedStateName);
    const value = data.point_lead * (data.winner === 'dem' ? -1 : 1);
    return value !== 0 ? dashboard.colorScale(value) : (data.winner === 'dem' ? dashboard.colorScale(-0.1) : dashboard.colorScale(0.1));
}

function getStrokeWidth(d: any, dashboard: Dashboard) {
    return dashboard.selectedStates.has(normalizeStateName(d.properties.name)) ? 2 : 0.5;
}

export function render_map(
    usGeoData: FeatureCollection<Geometry> | null,
    svgRef: React.MutableRefObject<SVGSVGElement | null>,
    mapState: USMapState,
    stateFeatures: any[],
    xScale: number,
    yScale: number,
    dashboard: Dashboard,
    updateSelectedStates: (states: Set<string>) => void,
    setTooltipData: (data: USMapInteractionData | null) => void
) {
    if (!usGeoData || !svgRef.current) return;

    const svg = d3.select(svgRef.current);
    const projection = d3.geoAlbersUsa().scale(1000).translate([480, 250]);
    const path = d3.geoPath().projection(projection);

    // Ensure we have a group element
    if (svg.select('g').empty()) {
        svg.append('g');
    }
    const g = svg.select('g');

    // Select and bind data
    const paths = g.selectAll<SVGPathElement, any>('path').data(stateFeatures, (d: any) => d.properties.name);
    // ENTER: New elements
    const enterPaths = paths.enter()
        .append('path')
        .attr('d', (d: any) => getPath(d, mapState, path))
        .attr('fill', d => getColor(d, dashboard))
        .attr('stroke', 'black')
        .attr('stroke-width', d => getStrokeWidth(d, dashboard));

    // MERGE: Combine enter + update selections
    const allPaths = enterPaths.merge(paths)
        // Rebind ALL event handlers
        .on('click', function(event, d) {
            const stateName = normalizeStateName(d.properties.name);
            if (dashboard.selectedStates.has(stateName)) {
                dashboard.selectedStates.delete(stateName);
            } else {
                dashboard.selectedStates.add(stateName);
            }
            updateSelectedStates(new Set(dashboard.selectedStates));
        })
        .on('mouseover', function(event, d) {
            d3.select(this).attr('stroke-width', 2);
            const tooltipData = createTooltipData(d, dashboard);
            setTooltipData({
                data: tooltipData,
                value: dashboard.selectedElectionData.getStateElectionData(
                    normalizeStateName(d.properties.name) as keyof StateElectionDataEntry
                ).point_lead * (
                    dashboard.selectedElectionData.getStateElectionData(
                        normalizeStateName(d.properties.name) as keyof StateElectionDataEntry
                    ).winner === 'dem' ? -1 : 1
                ),
                position: { x: event.clientX - 670, y: event.clientY }
            });
        })
        .on('mousemove', function(event, d) {
            const tooltipData = createTooltipData(d, dashboard);
            setTooltipData({
                data: tooltipData,
                value: dashboard.selectedElectionData.getStateElectionData(
                    normalizeStateName(d.properties.name) as keyof StateElectionDataEntry
                ).point_lead * (
                    dashboard.selectedElectionData.getStateElectionData(
                        normalizeStateName(d.properties.name) as keyof StateElectionDataEntry
                    ).winner === 'dem' ? -1 : 1
                ),
                position: { x: event.clientX - 670, y: event.clientY }
            });
        })
        .on('mouseout', function(event, d) {
            d3.select(this).attr('stroke-width', d => getStrokeWidth(d, dashboard));
            const containerRect = svgRef.current?.getBoundingClientRect();
            if (containerRect) {
                const isMouseOutsideContainer =
                    event.clientX < containerRect.left ||
                    event.clientX > containerRect.right ||
                    event.clientY < containerRect.top ||
                    event.clientY > containerRect.bottom;
                if (isMouseOutsideContainer) {
                    setTooltipData(null);
                }
            }
            setTooltipData(null);
        });

    // Apply transitions to all paths
    allPaths
        .transition()
        .duration(1000)
        .attr('stroke-width', d => getStrokeWidth(d, dashboard))
        .attrTween('d', function(d: any) {
            const currentPath: string = d3.select(this).attr('d');
            const targetPath = getPath(d, mapState, path);
            return interpolate(currentPath, targetPath);
        })
        .attrTween('fill', function(d: any) {
            const startColor:string = d3.select(this).attr('fill');
            const endColor = getColor(d, dashboard);
            return d3.interpolate(startColor, endColor);
        });

    // Remove old elements
    paths.exit().remove();

    // Handle Dorling cartogram if needed
    if (mapState.mapType !== USMapType.GEOGRAPHIC) {
        const nodes = stateFeatures.map((d: any) => ({
            properties: d.properties,
            x: d.centroid[0],
            y: d.centroid[1],
            r: mapState.getCircleRadius(normalizeStateName(d.properties.name) as keyof StateElectionDataEntry)
        }));

        const simulation = d3.forceSimulation(nodes)
            .force('x', d3.forceX((d: any) => d.x).strength(0.1))
            .force('y', d3.forceY((d: any) => d.y).strength(0.1))
            .force('collision', d3.forceCollide((d: any) => d.r + 1))
            .stop();

        for (let i = 0; i < 120; i++) simulation.tick();

        // Update paths for Dorling
        svg.selectAll('path')
            .data(nodes, (d: any) => d.properties.name)
            .transition()
            .duration(1000)
            .attrTween('d', function(d: any) {
                const currentPath:string = d3.select(this).attr('d');
                const targetPath = circlePath(d.x, d.y, d.r);
                return interpolate(currentPath, targetPath);
            })
            .attrTween('fill', function(d: any) {
                const startColor:string = d3.select(this).attr('fill');
                const endColor = getColor(d, dashboard);
                return d3.interpolate(startColor, endColor);
            })
            .attr('stroke-width', d => getStrokeWidth(d, dashboard));

        // Handle text labels
        const texts = g.selectAll<SVGTextElement, any>('text').data(nodes, (d: any) => d.properties.name);
        texts.enter()
            .append('text')
            .merge(texts)
            .attr('x', (d: any) => d.x)
            .attr('y', (d: any) => d.y)
            .attr('dy', '0.35em')
            .attr('text-anchor', 'middle')
            .text((d: any) => USElectionData.getStateInitials(d.properties.name))
            .attr('font-size', '10px')
            .attr('fill', 'black')
            .style('user-select', 'none')
            .style('cursor', 'default')
            .on('mousemove', function(event, d) {
                const tooltipData = createTooltipData(d, dashboard);
                setTooltipData({
                    data: tooltipData,
                    value: dashboard.selectedElectionData.getStateElectionData(
                        normalizeStateName(d.properties.name) as keyof StateElectionDataEntry
                    ).point_lead * (
                        dashboard.selectedElectionData.getStateElectionData(
                            normalizeStateName(d.properties.name) as keyof StateElectionDataEntry
                        ).winner === 'dem' ? -1 : 1
                    ),
                    position: { x: event.clientX - 670, y: event.clientY }
                });
            })
            .on('mouseover', function(event, d) {
                svg.selectAll('path')
                    .filter((p: any) => p.properties.name === d.properties.name)
                    .attr('stroke-width', 2);
            })
            .on('mouseout', function(event, d) {
                setTooltipData(null);
                svg.selectAll('path')
                    .filter((p: any) => p.properties.name === d.properties.name)
                    .attr('stroke-width', p => getStrokeWidth(p, dashboard));
                setTooltipData(null);
            });

// Update text positions for Dorling
        svg.selectAll('text')
            .data(nodes, (d: any) => d.properties.name)
            .transition()
            .duration(1000)
            .attr('x', (d: any) => d.x)
            .attr('y', (d: any) => d.y);

        texts.exit().remove();
    } else {
        g.selectAll('text').remove();
    }

    // Apply scaling transformation
    g.attr('transform', `scale(${xScale}, ${yScale})`);
}